import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { VisaPack } from "../types/visa";
import { API_URL } from "../config";

const ListVisaPacks = () => {
  const [visaPacks, setVisaPacks] = useState<VisaPack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // PAGINATION (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalItems, setTotalItems] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // Modals & Selection
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState<VisaPack | null>(null);

  const navigate = useNavigate();
  const API_URL_main = `${API_URL}/visa-packs`;

     const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // 1. Fetch Visa Packs (server-side pagination + filters)
  const fetchVisaPacks = async (page: number = 1) => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_URL_main, {
        params: {
          page,
          per_page: itemsPerPage,
          search: searchTerm || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
        },
        ...config_token 
      });

      const paginator = response.data.data; 
      setVisaPacks(Array.isArray(paginator?.data) ? paginator.data : []);
      setTotalItems(paginator?.total || 0);
      setLastPage(paginator?.last_page || 1);
    } catch (error) {
      console.error("Error fetching visa packs:", error);
      toast.error("Error fetching visa packs");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch whenever the page changes
  useEffect(() => {
    fetchVisaPacks(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Reset to page 1 whenever search/status change (skip on first mount,
  // since the [currentPage] effect above already fetches page 1 initially)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1); // triggers the [currentPage] effect -> fetch
    } else {
      fetchVisaPacks(1); // already on page 1, fetch manually
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      setCurrentPage(page);
    }
  };

  // Helper pour la pagination avec ellipses ("...")
  const getPaginationRange = (currentPage: number, totalPages: number) => {
    const delta = 1;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      } else if (
        range[range.length - 1] !== "..." &&
        (i < currentPage - delta || i > currentPage + delta)
      ) {
        range.push("...");
      }
    }
    return range;
  };

  // 2. Actions Handlers
  const handleDeleteClick = (pack: VisaPack) => {
    setSelectedPack(pack);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPack) return;

    try {
      await axios.delete(`${API_URL_main}/${selectedPack.id}` , config_token );
      toast.success("Visa pack deleted successfully");
      // Refetch current page so pagination stays in sync with the server total
      // (if it was the last item on the page, go back one page)
      if (visaPacks.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        fetchVisaPacks(currentPage);
      }
    } catch (error: any) {
      console.error("Error deleting visa pack:", error);
      toast.error("Error deleting visa pack");
    } finally {
      setShowDeleteModal(false);
      setSelectedPack(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/edit-pack/${id}`);
  };

  const handleToggleStatus = async (pack: VisaPack) => {
    try {
      // Endpoint dédié (PATCH) plutôt qu'un PUT complet — évite de renvoyer tous les
      // champs requis (name_en, price, documents...) juste pour changer le statut.
      const response = await axios.patch(`${API_URL_main}/${pack.id}/toggle-active`, config_token );

      if (response.data.status) {
        toast.success(`Visa pack ${!pack.is_active ? "activated" : "deactivated"} successfully`);
        setVisaPacks((prev) =>
          prev.map((p) => (p.id === pack.id ? { ...p, is_active: !p.is_active } : p))
        );
        // If a status filter is active, the toggled item may no longer belong
        // on this page — resync with the server to be safe.
        if (statusFilter !== "all") {
          fetchVisaPacks(currentPage);
        }
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Error updating visa pack status");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setStatusFilter("all");
  };

  // ── Skeleton Loader Component ──────────────────────────────────────────────
  function SkeletonLoader() {
    return (
      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>

        {/* Header Skeleton */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div style={{
                width: 200,
                height: 28,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: 60,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div style={{
              width: 300,
              height: 18,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 150,
            height: 42,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 8,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>

        {/* Main Card Skeleton */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          marginTop: "15px",
        }}>
          {/* Filter Bar Skeleton */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-6 col-lg-5">
                <div style={{
                  width: "100%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
              <div className="col-12 col-md-4 col-lg-3">
                <div style={{
                  width: "100%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            </div>
          </div>

          {/* Table Header Skeleton */}
          <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <div className="row">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="col" style={{ padding: "0 8px" }}>
                  <div style={{
                    width: i === 0 ? 80 : i === 1 ? 100 : i === 2 ? 140 : i === 3 ? 90 : i === 4 ? 120 : i === 5 ? 60 : i === 6 ? 70 : 60,
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Rows Skeleton */}
          {[...Array(8)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                padding: "14px 16px",
                borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
                display: "flex",
                alignItems: "center"
              }}
            >
              {[...Array(8)].map((_, colIndex) => (
                <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                  <div style={{
                    width: colIndex === 0 ? 120 : colIndex === 1 ? 100 : colIndex === 2 ? 150 : colIndex === 3 ? 80 : colIndex === 4 ? 100 : colIndex === 5 ? 80 : colIndex === 6 ? 70 : 120,
                    height: 16,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          ))}

          {/* Pagination Skeleton */}
          <div style={{
            padding: "12px 20px",
            borderTop: "1px solid #edf2f7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{
              width: 200,
              height: 16,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ display: "flex", gap: "6px" }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  width: i === 0 || i === 4 ? 70 : 32,
                  height: 32,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 6,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Manage Visa Packs
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p
            style={{
              color: "#64748b",
              fontSize: "13.5px",
              margin: 0,
              fontWeight: 400,
            }}
          >
            Overview and configuration of{" "}
            <strong style={{ color: "#294b5a" }}>{totalItems}</strong> total
            visa packs.
          </p>
        </div>

        {/* Bouton Add Visa Pack */}
        <button
          onClick={() => navigate("/admin/add-pack")}
          style={{
            backgroundColor: "#294b5a",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#1e3743";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(41, 75, 90, 0.35)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#294b5a";
            e.currentTarget.style.boxShadow =
              "0 2px 8px rgba(41, 75, 90, 0.25)";
          }}
        >
          <i
            className="fa-solid fa-plus"
            style={{ color: "#fff", fontSize: "13px" }}
          ></i>
          <span>Add Visa Pack</span>
        </button>
      </div>

      {/* BLOC PRINCIPAL CONTENANT FILTRES + TABLE */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #edf2f7",
            backgroundColor: "#ffffff",
          }}
        >
          <div className="row g-3 align-items-center">
            {/* Recherche */}
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search visa packs by name, destination, nationality..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                    transition: "all 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#294b5a";
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.backgroundColor = "#f8fafc";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      padding: "0",
                    }}
                  >
                    <i
                      className="fa-solid fa-xmark"
                      style={{ fontSize: "14px" }}
                    ></i>
                  </button>
                )}
              </div>
            </div>

            {/* Filtre de Statut */}
            <div className="col-12 col-md-4 col-lg-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#334155",
                  outline: "none",
                  backgroundColor: "#f8fafc",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#294b5a";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#cbd5e1";
                  e.target.style.backgroundColor = "#f8fafc";
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Name
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Destination
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Eligible Nationalities
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Visa Type
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Processing Time
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Price
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "center",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        color: "#294b5a",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        textAlign: "right",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visaPacks.length > 0 ? (
                    visaPacks.map((pack) => {
                      const nationalities = pack.nationalities || [];
                      const nationalityLabel =
                        nationalities.length === 0
                          ? "Verify live"
                          : nationalities.length <= 2
                          ? nationalities.map((n) => n.name_en || n.name).join(", ")
                          : `${nationalities.length} nationalities`;

                      return (
                        <tr
                          key={pack.id}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background-color 0.15s ease",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = "#fafaf8")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor = "transparent")
                          }
                        >
                          {/* Name */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              fontWeight: 600,
                              color: "#0f172a",
                            }}
                          >
                            {pack.name_en}
                          </td>

                          {/* Destination */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              color: "#475569",
                            }}
                          >
                            {pack.destination_country?.name_en || pack.destination_country?.name || "--"}
                          </td>

                          {/* Eligible Nationalities */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              color: "#475569",
                            }}
                            title={nationalities.map((n) => n.name_en || n.name).join(", ")}
                          >
                            {nationalityLabel}
                          </td>

                          {/* Visa Type */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              color: "#475569",
                            }}
                          >
                            {pack.visa_type?.name || pack.visa_type?.name_en || "--"}
                          </td>

                          {/* Processing Time */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              color: "#475569",
                            }}
                          >
                            {pack.processing_time_en || "--"}
                          </td>

                          {/* Price */}
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "13.5px",
                              fontWeight: 600,
                              color: "#294b5a",
                            }}
                          >
                            {pack.price ? `${pack.price} ${pack.currency || "OMR"}` : "--"}
                          </td>

                          {/* Status */}
                          <td
                            style={{
                              padding: "14px 16px",
                              textAlign: "center",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: pack.is_active
                                  ? "#f0fdf4"
                                  : "#fef2f2",
                                color: pack.is_active ? "#15803d" : "#b91c1c",
                                border: `1px solid ${
                                  pack.is_active ? "#bbf7d0" : "#fecaca"
                                }`,
                              }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  backgroundColor: pack.is_active
                                    ? "#16a34a"
                                    : "#dc2626",
                                }}
                              ></span>
                              {pack.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>

                          {/* Actions */}
                          <td
                            style={{
                              padding: "14px 16px",
                              textAlign: "right",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              {/* Toggle Status */}
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(pack)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: pack.is_active ? "#dc2626" : "#16a34a",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    pack.is_active ? "#fef2f2" : "#f0fdf4";
                                  e.currentTarget.style.borderColor =
                                    pack.is_active ? "#fecaca" : "#bbf7d0";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                }}
                                title={pack.is_active ? "Deactivate" : "Activate"}
                              >
                                <i
                                  className={`fa-solid ${
                                    pack.is_active
                                      ? "fa-ban"
                                      : "fa-circle-check"
                                  }`}
                                  style={{ fontSize: "13px" }}
                                ></i>
                              </button>

                              {/* Edit Button */}
                              <button
                                type="button"
                                onClick={() => handleEditClick(pack.id)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#294b5a",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#f8fafc";
                                  e.currentTarget.style.borderColor = "#cbd5e1";
                                  e.currentTarget.style.color = "#d3be76";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#294b5a";
                                }}
                                title="Edit"
                              >
                                <i
                                  className="fa-solid fa-pen-to-square"
                                  style={{ fontSize: "13px" }}
                                ></i>
                              </button>

                              {/* Delete Button */}
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(pack)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#64748b",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#fef2f2";
                                  e.currentTarget.style.borderColor = "#fecaca";
                                  e.currentTarget.style.color = "#dc2626";
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#ffffff";
                                  e.currentTarget.style.borderColor = "#e2e8f0";
                                  e.currentTarget.style.color = "#64748b";
                                }}
                                title="Delete"
                              >
                                <i
                                  className="fa-solid fa-trash-can"
                                  style={{ fontSize: "13px" }}
                                ></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "48px 20px",
                          color: "#64748b",
                          fontSize: "13.5px",
                        }}
                      >
                        <i
                          className="fa-solid fa-passport mb-2"
                          style={{
                            fontSize: "24px",
                            color: "#cbd5e1",
                            display: "block",
                          }}
                        ></i>
                        No visa packs found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* BARRE DE PAGINATION PAR 10 (server-side) */}
            {totalItems > 0 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Showing{" "}
                  <strong>
                    {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}
                  </strong>{" "}
                  to{" "}
                  <strong>
                    {Math.min(currentPage * itemsPerPage, totalItems)}
                  </strong>{" "}
                  of <strong>{totalItems}</strong> entries
                </div>

                {lastPage > 1 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {/* Bouton Précédent */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Previous
                    </button>

                    {/* Numéros de page avec Ellipses */}
                    {getPaginationRange(currentPage, lastPage).map(
                      (item, index) => {
                        if (item === "...") {
                          return (
                            <span
                              key={`dots-${index}`}
                              style={{
                                padding: "0 6px",
                                fontSize: "13px",
                                color: "#94a3b8",
                                fontWeight: "600",
                                userSelect: "none",
                              }}
                            >
                              ...
                            </span>
                          );
                        }

                        const pageNum = item as number;
                        const isActive = currentPage === pageNum;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "12.5px",
                              fontWeight: "600",
                              borderRadius: "6px",
                              border: isActive ? "none" : "1px solid #cbd5e1",
                              backgroundColor: isActive ? "#294b5a" : "#ffffff",
                              color: isActive ? "#ffffff" : "#334155",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    )}

                    {/* Bouton Suivant */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === lastPage}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12.5px",
                        fontWeight: "600",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                        color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                        cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal: Confirmation de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* MODAL BODY */}
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                }}
              >
                <i
                  className="fa-solid fa-trash-can"
                  style={{ color: "#dc2626", fontSize: "22px" }}
                ></i>
              </div>

              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#294b5a",
                  margin: "0 0 8px 0",
                  letterSpacing: "-0.01em",
                }}
              >
                Delete Visa Pack?
              </h2>

              <p
                style={{
                  fontSize: "13.5px",
                  color: "#64748b",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to delete this visa pack? This action cannot
                be undone.
              </p>

              {/* TARGET PACK PREVIEW CARD */}
              {selectedPack && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13.5px",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {selectedPack.name_en}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Destination: {selectedPack.destination_country?.name_en || selectedPack.destination_country?.name || "N/A"}
                  </div>
                </div>
              )}
            </div>

            {/* MODAL FOOTER */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px 20px 24px",
                backgroundColor: "#ffffff",
              }}
            >
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  fontSize: "13.5px",
                  fontWeight: "600",
                  color: "#ffffff",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#b91c1c";
                  e.currentTarget.style.boxShadow =
                    "0 4px 10px rgba(220, 38, 38, 0.35)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(220, 38, 38, 0.25)";
                }}
              >
                <i
                  className="fa-solid fa-trash-can"
                  style={{ fontSize: "13px" }}
                ></i>
                Delete Pack
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListVisaPacks;